/**
 * Export Aquifer Summary to CSV
 */

import { COLUMN_MODES, GALLONS_PER_ACRE_FOOT } from "../constants";
import { downloadCSV } from "./downloadCSV";

/**
 * Export aquifer summary data to CSV
 * @param {import("../types").AquiferSummary} summary - Aquifer summary data
 * @param {string} columnMode - Column display mode
 */
export const exportAquiferSummary = (summary, columnMode) => {
  const headers =
    columnMode === COLUMN_MODES.compact
      ? [
          "Aquifer",
          "Year",
          "Agg System",
          "Hist. Permit (ac-ft)",
          "Oper. Permit (ac-ft)",
          "Total Permit (ac-ft)",
          "YTD (gal)",
          "Remaining (gal)",
          "YTD (ac-ft)",
          "Remaining (ac-ft)",
          "% Permit",
        ]
      : [
          "Aquifer",
          "Year",
          "Agg System",
          "Hist. Permit (ac-ft)",
          "Oper. Permit (ac-ft)",
          "Total Permit (ac-ft)",
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
          "YTD (gal)",
          "Remaining (gal)",
          "YTD (ac-ft)",
          "Remaining (ac-ft)",
          "% Permit",
        ];

  const rows = [headers];

  summary.aquifers.forEach((aq) => {
    const remainingGal =
      aq.totals.h_permit_value_gal +
      aq.totals.o_permit_value_gal -
      aq.totals.ytd_gal;
    const remainingAf = remainingGal / GALLONS_PER_ACRE_FOOT;

    const baseRow = [
      aq.aquifer_name,
      "",
      "",
      aq.totals.h_permit_af,
      aq.totals.o_permit_af,
      aq.totals.total_permit_af,
    ];

    if (columnMode === COLUMN_MODES.full) {
      baseRow.push(
        aq.totals.jan,
        aq.totals.feb,
        aq.totals.mar,
        aq.totals.apr,
        aq.totals.may,
        aq.totals.jun,
        aq.totals.jul,
        aq.totals.aug,
        aq.totals.sep,
        aq.totals.oct,
        aq.totals.nov,
        aq.totals.dec
      );
    }

    baseRow.push(
      aq.totals.ytd_gal,
      remainingGal,
      aq.totals.ytd_af,
      remainingAf,
      aq.pct_permit
    );

    rows.push(baseRow);

    aq.years.forEach((yr) => {
      const yrRemainingGal =
        yr.totals.h_permit_value_gal +
        yr.totals.o_permit_value_gal -
        yr.totals.ytd_gal;
      const yrRemainingAf = yrRemainingGal / GALLONS_PER_ACRE_FOOT;

      const yearRow = [
        aq.aquifer_name,
        yr.year,
        "",
        yr.totals.h_permit_af,
        yr.totals.o_permit_af,
        yr.totals.total_permit_af,
      ];

      if (columnMode === COLUMN_MODES.full) {
        yearRow.push(
          yr.totals.jan,
          yr.totals.feb,
          yr.totals.mar,
          yr.totals.apr,
          yr.totals.may,
          yr.totals.jun,
          yr.totals.jul,
          yr.totals.aug,
          yr.totals.sep,
          yr.totals.oct,
          yr.totals.nov,
          yr.totals.dec
        );
      }

      yearRow.push(
        yr.totals.ytd_gal,
        yrRemainingGal,
        yr.totals.ytd_af,
        yrRemainingAf,
        yr.pct_permit
      );

      rows.push(yearRow);

      if (yr.aggSystems) {
        yr.aggSystems.forEach((agg) => {
          const aggRemainingGal =
            agg.totals.h_permit_value_gal +
            agg.totals.o_permit_value_gal -
            agg.totals.ytd_gal;
          const aggRemainingAf = aggRemainingGal / GALLONS_PER_ACRE_FOOT;

          const aggRow = [
            aq.aquifer_name,
            yr.year,
            agg.agg_system_name,
            agg.totals.h_permit_af,
            agg.totals.o_permit_af,
            agg.totals.total_permit_af,
          ];

          if (columnMode === COLUMN_MODES.full) {
            aggRow.push(
              agg.totals.jan,
              agg.totals.feb,
              agg.totals.mar,
              agg.totals.apr,
              agg.totals.may,
              agg.totals.jun,
              agg.totals.jul,
              agg.totals.aug,
              agg.totals.sep,
              agg.totals.oct,
              agg.totals.nov,
              agg.totals.dec
            );
          }

          aggRow.push(
            agg.totals.ytd_gal,
            aggRemainingGal,
            agg.totals.ytd_af,
            aggRemainingAf,
            agg.pct_permit
          );

          rows.push(aggRow);
        });
      }
    });
  });

  // Grand Totals row
  const grandRemainingGal =
    summary.grandTotals.h_permit_value_gal +
    summary.grandTotals.o_permit_value_gal -
    summary.grandTotals.ytd_gal;
  const grandRemainingAf = grandRemainingGal / GALLONS_PER_ACRE_FOOT;

  const grandRow = [
    "GRAND TOTAL",
    "",
    "",
    summary.grandTotals.h_permit_af,
    summary.grandTotals.o_permit_af,
    summary.grandTotals.total_permit_af,
  ];

  if (columnMode === COLUMN_MODES.full) {
    grandRow.push(
      summary.grandTotals.jan,
      summary.grandTotals.feb,
      summary.grandTotals.mar,
      summary.grandTotals.apr,
      summary.grandTotals.may,
      summary.grandTotals.jun,
      summary.grandTotals.jul,
      summary.grandTotals.aug,
      summary.grandTotals.sep,
      summary.grandTotals.oct,
      summary.grandTotals.nov,
      summary.grandTotals.dec
    );
  }

  grandRow.push(
    summary.grandTotals.ytd_gal,
    grandRemainingGal,
    summary.grandTotals.ytd_af,
    grandRemainingAf,
    summary.grandTotals.pct_permit
  );

  rows.push(grandRow);

  downloadCSV(rows, "aquifer-summary.csv");
};
